import React, { useEffect, useRef, useState } from 'react';
import { Icons } from '@/src/modules/components/Icons';
import { usePublishEntitlement } from '@/src/modules/entitlements/usePublishEntitlement';
import { useCreatePurchase } from '@/src/modules/purchase/useCreatePurchase';
import { useCreditPacks } from '../useCreditPacks';
import { useWallet } from '../useWallet';
import type { CreditPackView } from '../creditPacks.api';
import { CreditPackCard } from './CreditPackCard';
import { formatPrice } from '@/src/infrastructure/lib/currency';

function mintIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `pack-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function PartnerCreditPacksSection() {
  const { packs, loading: loadingPacks, error: packsError, reload: reloadPacks } = useCreditPacks();
  const { reload: reloadEntitlement } = usePublishEntitlement();
  const { wallet, reload: reloadWallet } = useWallet();
  const purchaseFlow = useCreatePurchase();
  const [selectedPackCode, setSelectedPackCode] = useState<string | null>(null);
  const errorBannerRef = useRef<HTMLDivElement>(null);

  // PQ-47: When a rate-limit or purchase error occurs, scroll the error banner into view
  useEffect(() => {
    if (purchaseFlow.error) {
      errorBannerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [purchaseFlow.error]);

  // Selection is the user's choice and drives the card highlight (`isSelected`).
  // It is set here and never reset on failure, so a failed attempt leaves the pack
  // they picked still visibly selected rather than snapping the highlight away.
  const handleSelectPack = async (pack: CreditPackView) => {
    setSelectedPackCode(pack.code);
    const key = mintIdempotencyKey();
    const created = await purchaseFlow.createPurchaseStep({
      packCode: pack.code,
      idempotencyKey: key,
    });

    if (created) {
      const freshCheckout = await purchaseFlow.proceedToCheckout(created);
      if (freshCheckout) {
        // Pass BOTH explicitly: this handler holds the click-time closure, where
        // the hook's `purchase`/`checkout` state is still null on a first purchase.
        await purchaseFlow.openPayment(freshCheckout, created);
      }
    }
  };

  const isPurchasing =
    purchaseFlow.phase === 'creating-purchase' ||
    purchaseFlow.phase === 'creating-checkout' ||
    purchaseFlow.phase === 'awaiting-gateway' ||
    purchaseFlow.phase === 'confirming';

  // When paid, reload the partner's publishing entitlement balance and clear selection
  React.useEffect(() => {
    if (purchaseFlow.phase === 'paid') {
      setSelectedPackCode(null);
      void reloadEntitlement();
      void reloadWallet();
    }
  }, [purchaseFlow.phase, reloadEntitlement, reloadWallet]);

  return (
    <div className="space-y-8" data-testid="partner-credit-packs-section">
      {/* Overview & Balance Card */}
      <div className="rounded-3xl bg-surface border border-outline-variant/30 p-6 md:p-8 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="p-2 rounded-xl bg-primary/10 text-primary">
                <Icons.Subscription size={20} />
              </span>
              <h2 className="text-2xl font-bold text-on-surface">Partner Credit Packs</h2>
            </div>
            <p className="text-sm text-on-surface-variant max-w-xl">
              Wholesale publishing credits for invitation agencies and event partners. Enjoy monotonic volume savings,
              permanent capability unlocks, and zero expiration.
            </p>
          </div>

          {wallet && wallet.balance > 0 && (
            <div className="flex flex-col items-start md:items-end justify-center p-4 rounded-2xl bg-primary/5 border border-primary/20 min-w-[200px]" data-testid="partner-wallet-header">
              <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Wallet balance</span>
              <span className="text-3xl font-extrabold text-on-surface mt-1" data-testid="partner-wallet-balance">
                ₹{wallet.balance.toLocaleString('en-IN')}
              </span>
              {wallet.blendedDiscountPercent !== null && (
                <span className="text-xs font-medium text-primary mt-1" data-testid="partner-wallet-rate">
                  Your blended rate: {wallet.blendedDiscountPercent}% off
                </span>
              )}
            </div>
          )}
        </div>

        {/* Guarantees Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-outline-variant/20">
          <div className="flex items-start gap-3">
            <Icons.CheckCircle size={18} className="text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-bold text-on-surface">No Expiration Date</p>
              <p className="text-xs text-on-surface-variant">Credits stay yours until spent. No calendar forfeiture.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icons.CheckCircle size={18} className="text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-bold text-on-surface">Only Pay When Empty</p>
              <p className="text-xs text-on-surface-variant">No recurring auto-debit. Top up on your schedule.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Success Banner */}
      {purchaseFlow.phase === 'paid' && (
        <div
          role="status"
          className="rounded-2xl bg-status-published/10 border border-status-published/30 p-6 flex items-start gap-4"
          data-testid="purchase-success-banner"
        >
          <Icons.CheckCircle size={24} className="text-status-published shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-base font-bold text-on-surface">
              Payment Successful!
              {purchaseFlow.purchase?.orderNumber && (
                <span className="ml-2 text-xs font-normal text-on-surface-variant font-mono">
                  ({purchaseFlow.purchase.orderNumber})
                </span>
              )}
            </h4>
            <p className="text-sm text-on-surface-variant">
              Your credits have been added to your balance. Your permanent feature unlocks are active.
            </p>
            <p className="text-sm text-on-surface-variant">
              Want more credit? Choose a pack below to top up again.
            </p>
            <button
              type="button"
              onClick={purchaseFlow.reset}
              className="mt-2 text-xs font-semibold text-primary hover:underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Cancelled Order Banner */}
      {purchaseFlow.purchase?.status === 'CANCELLED' && (
        <div
          role="alert"
          className="rounded-2xl bg-status-failed/10 border border-status-failed/30 p-6 flex items-start gap-4"
        >
          <Icons.Warning size={24} className="text-status-failed shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-base font-bold text-on-surface">
              Order Cancelled
              {purchaseFlow.purchase.orderNumber && (
                <span className="ml-2 text-xs font-normal text-on-surface-variant font-mono">
                  ({purchaseFlow.purchase.orderNumber})
                </span>
              )}
            </h4>
            <p className="text-sm text-on-surface-variant">
              This order was cancelled. If you completed a payment, your money has been automatically refunded.
            </p>
            <button
              type="button"
              onClick={purchaseFlow.reset}
              className="mt-2 text-xs font-semibold text-primary hover:underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Ready for Payment / Retry Banner */}
      {purchaseFlow.phase === 'ready-for-payment' && purchaseFlow.checkout && purchaseFlow.purchase?.status !== 'CANCELLED' && (
        <div className="rounded-2xl bg-primary/10 border border-primary/30 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h4 className="text-base font-bold text-on-surface">Order Ready for Payment</h4>
            <p className="text-sm text-on-surface-variant">
              Amount payable:{' '}
              <span className="font-bold text-on-surface">
                {formatPrice(purchaseFlow.checkout.checkout.amountPaise, purchaseFlow.checkout.checkout.currency, {
                  exact: true,
                })}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => void purchaseFlow.openPayment()}
            className="py-2.5 px-6 rounded-xl bg-primary text-on-primary font-semibold text-sm shadow-sm hover:bg-primary/90"
          >
            Complete Payment Now
          </button>
        </div>
      )}

      {/* Error Banner */}
      {purchaseFlow.error && (
        <div
          ref={errorBannerRef}
          role="alert"
          className="rounded-2xl bg-status-failed/10 border border-status-failed/30 p-4 text-status-failed text-sm font-medium flex items-center justify-between gap-4"
        >
          <span>{purchaseFlow.error}</span>
          <button
            type="button"
            onClick={purchaseFlow.reset}
            className="text-xs font-bold text-status-failed hover:underline uppercase"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Credit Pack Cards Grid */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-bold text-on-surface">Select a Credit Pack</h3>
        </div>

        {loadingPacks && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" aria-busy="true">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 rounded-3xl bg-surface border border-outline-variant/30 animate-pulse" />
            ))}
          </div>
        )}

        {!loadingPacks && packsError && (
          <div className="rounded-3xl border border-outline-variant/30 bg-surface p-12 text-center space-y-4">
            <p className="text-sm text-status-failed font-medium">{packsError}</p>
            <button
              type="button"
              onClick={() => void reloadPacks()}
              className="text-sm font-semibold text-primary hover:underline"
            >
              Retry loading packs
            </button>
          </div>
        )}

        {!loadingPacks && !packsError && packs.length === 0 && (
          <div className="rounded-3xl border border-outline-variant/30 bg-surface p-12 text-center">
            <p className="text-sm text-on-surface-variant font-medium">No active credit packs available right now.</p>
          </div>
        )}

        {!loadingPacks && !packsError && packs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packs.map((pack) => (
              <CreditPackCard
                key={pack.id}
                pack={pack}
                onSelect={handleSelectPack}
                isBusy={isPurchasing && selectedPackCode === pack.code}
                isSelected={selectedPackCode === pack.code}
                errorMessage={selectedPackCode === pack.code ? purchaseFlow.error : null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
